/**
 * Passphrase prompt for sealed identity unlock.
 *
 * Two modes:
 *   - 'unlock': a sealed identity exists locally. User enters the
 *     passphrase they chose at claim time. If the device has an
 *     enrolled biometric credential, the "Unlock with biometrics"
 *     button appears above the passphrase field — one tap, Touch ID
 *     (or Face ID / Windows Hello / Android biometric / PIN) prompt,
 *     then the passphrase is unwrapped via WebAuthn PRF and handed
 *     to `onSubmit`. If biometrics fail or are absent, the
 *     passphrase input is still there as the fall-back.
 *   - 'create': no sealed identity yet (freshly claimed, or device-
 *     reset). User picks a new passphrase; we require confirmation to
 *     catch typos (there's no recovery if they forget it).
 *
 * Argon2id takes a few seconds on commodity hardware; the submit
 * button disables during derivation so users don't double-click.
 */
import { useState, type FormEvent } from 'react';

export interface PassphrasePromptProps {
  readonly mode: 'unlock' | 'create';
  readonly onSubmit: (passphrase: string) => Promise<void>;
  /**
   * When provided, a "Unlock with biometrics" button appears in
   * 'unlock' mode. The callback prompts WebAuthn, unwraps the
   * passphrase from IndexedDB, and drives the full unlock flow
   * itself — this component doesn't see the passphrase.
   */
  readonly onBiometricUnlock?: () => Promise<void>;
}

export function PassphrasePrompt({ mode, onSubmit, onBiometricUnlock }: PassphrasePromptProps) {
  const [passphrase, setPassphrase] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(undefined);
    if (passphrase.length < 8) {
      setError('passphrase must be at least 8 characters');
      return;
    }
    if (mode === 'create' && passphrase !== confirmation) {
      setError('passphrase and confirmation do not match');
      return;
    }
    setBusy(true);
    try {
      await onSubmit(passphrase);
      // Clear from state on success; if the callback navigated away
      // this is harmless, and if it didn't we don't want the passphrase
      // sitting in React state.
      setPassphrase('');
      setConfirmation('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unlock failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleBiometric() {
    if (!onBiometricUnlock || busy) return;
    setError(undefined);
    setBusy(true);
    try {
      await onBiometricUnlock();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'biometric unlock failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main data-testid="passphrase-prompt" className="landing">
      <section className="landing-hero" style={{ paddingBottom: 0 }}>
        <h1 className="landing-title" style={{ fontSize: 'clamp(2.4em, 10vw, 4.2em)' }}>
          GAME<span className="z">Z</span>
        </h1>
      </section>
      <section className="landing-card">
        <h2>{mode === 'create' ? 'Set your passphrase' : 'Welcome back'}</h2>
        <p className="faint" style={{ marginTop: '0.2em' }}>
          {mode === 'create'
            ? 'Your identity key is encrypted on this device. Pick a passphrase — you\u2019ll be prompted for it every time you open the app. There is no recovery, so write it down if you need to.'
            : 'Unlock your identity to keep playing.'}
        </p>
        {mode === 'unlock' && onBiometricUnlock && (
          <div className="vstack" style={{ marginBottom: '0.8em' }}>
            <button
              type="button"
              data-testid="biometric-unlock"
              className="primary"
              disabled={busy}
              onClick={() => void handleBiometric()}
              style={{ padding: '0.7em' }}
            >
              {busy ? 'prompting…' : '🔒 Unlock with biometrics'}
            </button>
            <p className="faint" style={{ textAlign: 'center', margin: '0.2em 0' }}>
              — or enter passphrase —
            </p>
          </div>
        )}
        <form onSubmit={(e) => void handleSubmit(e)} className="vstack">
          <label>
            <span className="landing-label">
              {mode === 'create' ? 'new passphrase' : 'passphrase'}
            </span>
            <input
              data-testid="passphrase-input"
              type="password"
              autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              disabled={busy}
              placeholder="at least 8 characters"
            />
          </label>
          {mode === 'create' && (
            <label>
              <span className="landing-label">confirm passphrase</span>
              <input
                data-testid="passphrase-confirm"
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                disabled={busy}
              />
            </label>
          )}
          {error && (
            <p data-testid="passphrase-error" style={{ color: 'var(--danger)' }}>
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="passphrase-submit"
            className="primary landing-submit"
            disabled={busy}
          >
            {busy ? 'deriving key…' : mode === 'create' ? 'save →' : 'unlock →'}
          </button>
        </form>
      </section>
    </main>
  );
}
